import { useEffect, type ReactNode } from "react";
import { CalcSteps } from "./CalcSteps";
import { ResultsTable, CALC_PRINT_CSS } from "./ResultsTable";
import { ExportBar } from "./ExportBar";
import { CalcHistory } from "./CalcHistory";
import { recordCalculation } from "@/lib/calc/history";
import { reportCsv, reportText, type ReportTable } from "@/lib/calc/report";
import type { CalcStep } from "@/lib/calc/steps";

/**
 * Everything a calculator needs under its result in one lazy-loaded block: "How this was calculated", one or more tables,
 * the shared gated export bar (Copy, CSV, Print), and the visit's history. The calculator builds `steps` and `tables` from
 * the SAME numbers it shows, so the working and the export can never disagree with the result.
 * `gate` is the tool's own gate from useLogToolUsage: nothing is delivered unless it allows it (one trial use per visit).
 */
export default function CalcReport({ title, tool, summary, steps, tables, notes, gate, filename, children }: {
  title: string; tool: string; summary: string; steps: CalcStep[]; tables: ReportTable[]; notes?: string[];
  gate: () => Promise<boolean>; filename: string; children?: ReactNode;
}) {
  useEffect(() => {
    const t = setTimeout(() => recordCalculation(tool, summary), 800);
    return () => clearTimeout(t);
  }, [tool, summary]);
  return (
    <div className="mt-8 space-y-6 calc-print-area" data-testid="calc-report">
      <style>{CALC_PRINT_CSS}</style>
      <CalcSteps steps={steps} />
      {children}
      {tables.map((t) => (
        <section key={t.caption} aria-label={t.heading} className="space-y-2">
          <h3 className="text-base font-semibold">{t.heading}</h3>
          <ResultsTable caption={t.caption} rows={t.rows} columns={t.columns} rowKey={t.rowKey} maxHeightClass="max-h-[520px]" />
        </section>
      ))}
      <ExportBar gate={gate} text={reportText({ title, summary, steps, tables, notes })} csv={reportCsv(tables)} filename={filename} label="report" />
      <CalcHistory tool={tool} />
    </div>
  );
}
